import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  Observable,
  of as observableOf,
  throwError as observableThrowError,
} from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import * as URI from 'urijs';

import { AppConfig } from '®shared/config';
import { RedirectionService } from '®shared/redirection';

import { AnonymousUser } from './anonymous-user';
import { AuthProvider } from './auth-provider';
import { AuthenticationFailedException } from './authentication-failed-exception';
import { UserScopesService } from './user-scopes.service';

export type AnonymousOauthResponse = {
  access_token: string;
  token_type: 'bearer';
};

@Injectable()
export class AnonymousAuthService extends AuthProvider<AnonymousUser> {
  // state used to track the current anonymous token, for token refresh
  private currentToken: string;

  constructor(
    public appConfig: AppConfig,
    private httpClient: HttpClient,
    private redirectionService: RedirectionService,
    router: Router,
    public userScopesService: UserScopesService,
  ) {
    super(appConfig, router, userScopesService);

    this.currentToken = this.appConfig.tenant.anonymousAccessToken;
  }

  /**
   * Authenticate a user with the current identity provider
   *
   * @return {Observable<AnonymousUser>} the user, if they are authenticated
   */
  getCurrentToken(_returnUrl?: string): Observable<AnonymousUser> {
    if (!this.appConfig.tenant.anonymousAccessToken) {
      throw new AuthenticationFailedException('No anonymous token for tenant');
    }

    return observableOf(
      new AnonymousUser(this.appConfig.tenant.anonymousAccessToken),
    );
  }

  /**
   * Reauthenticate the user (unless currently in the process of reauthenticating)
   *
   * @return {Observable<AnonymousUser>} the user profile, once authenticated
   */
  refreshToken(): Observable<AnonymousUser> {
    if (!this.appConfig.tenant.anonymousAccessToken) {
      throw new AuthenticationFailedException('No anonymous token for tenant');
    }

    // keep `currentToken` in sync with the current token.
    return this.exchangeToken().pipe(
      tap(user => {
        this.currentToken = user.accessToken;
      }),
    );
  }

  /**
   * Perform user login - in this case, by token exchange through an API
   *
   * @param  {?string}                    returnUrl An optional return URL
   * @return {Observable<AnonymousUser>}            the user, once authenticated
   */
  obtainNewToken(returnUrl?: string): Observable<AnonymousUser> {
    if (!this.appConfig.tenant.anonymousAccessToken) {
      throw new AuthenticationFailedException('No anonymous token for tenant');
    }

    const url = returnUrl || this.getCurrentUri().toString();
    this.redirectionService.redirectToExternalUrl(url);

    return observableThrowError(
      new AuthenticationFailedException('Redirecting to obtain token'),
    );
  }

  /**
   * Exchange the current anonymous token for a new token (refresh)
   *
   * @return {Observable<AnonymousUser>} the user, following token refresh
   */
  exchangeToken(): Observable<AnonymousUser> {
    const handleError = error => {
      return observableThrowError(
        new AuthenticationFailedException(
          (error && error.message) || 'Unknown authentication error',
        ),
      );
    };

    const headers = this.getHeaders();
    return this.httpClient
      .get<AnonymousOauthResponse>(this.tokenRefreshUrl(), { headers })
      .pipe(
        map(response => {
          const { access_token = undefined } = response;
          if (!access_token) {
            throw new AuthenticationFailedException(
              `Not a valid OAuth JSON response: ${response}`,
            );
          }
          return new AnonymousUser(access_token);
        }),
        catchError(handleError),
      );
  }

  // -- helper methods

  private getHeaders(): HttpHeaders {
    const headersWithAuthorization = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...this.getBearerHeader(this.currentToken),
    };

    return new HttpHeaders(headersWithAuthorization);
  }

  /**
   * Get the base URL for all V3 API routes for the currently-configured tenant.
   *
   * @return {string} An absolute URL to a V3 API base endpoint (for tenant)
   */
  private tokenRefreshUrl(): string {
    return new URI(this.appConfig.apiEndpoint)
      .segment(`${this.appConfig.tenant.shortCode}/token`)
      .toString();
  }
}
